import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { AccountToEdit, State } from "./types";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import TagsInput from "./TagsInput";
import isEmail from "utils/isEmail";
import { useAccountState } from "pages/Accounts/accountContext";
import ApprovalGroupSelector from "./ApprovalGroupSelector";
import { Group } from "@dashboard-v3/api";
import ButtonWithLoading from "components/ButtonWithLoading";
import styled from "styled-components";
import ChipInput from "material-ui-chip-input";
import handleValidationErrors from "./handleValidationErrors";
import useStorageAccountValidation from "utils/useStorageAccountValidation";
import useApprovalGroups from "pages/Accounts/AccountList/useApprovalGroups";
import { Skeleton } from "@material-ui/lab";

type Props = {
  open: boolean;
  onClose: () => void;
  account?: AccountToEdit;
};

export default function FormDialog({ open, onClose, account }: Props) {
  const { t } = useTranslation("accounts");
  const [emailError, setEmailError] = useState<string>(null);
  const [state, setState] = useState<State>(() => initState(account));
  const [aliasesError, setAliasesError] = useState<string>(null);
  const { state: accountsState, actions } = useAccountState();
  const [storageError, setStorageError] = useState(initStorageError);
  const [isSaving, setIsSaving] = useState(false);
  const validateAccess = useStorageAccountValidation();

  const { isLoading, approvalGroups } = useApprovalGroups(account, false);

  const handleChange = event => {
    const { value, name } = event.target;

    if (name === "email") {
      setEmailError(null);
    }
    if (name === "aliases") {
      setAliasesError(null);
    }

    setState(current => ({ ...current, [name]: value }));
  };

  const validateAliases = aliases => {
    const wrongEmails = aliases.filter(email => !isEmail(email));

    if (wrongEmails.length > 0) {
      setAliasesError(`${t("error.aliasesAreWrong")} ${wrongEmails}`);
    } else {
      setAliasesError(null);
    }
  };

  const handleAddAlias = newAlias => {
    const newAliases = [...state.aliases, newAlias];
    validateAliases(newAliases);

    setState(current => ({
      ...current,
      aliases: newAliases,
    }));
  };

  const handleDeleteAlias = removedAlias => {
    const newAliases = state.aliases.filter(alias => alias !== removedAlias);
    validateAliases(newAliases);
    setState(current => ({ ...current, aliases: newAliases }));
  };

  const handleAddTag = (_, selected) => {
    setState(prev => ({ ...prev, tags: selected }));
  };

  const handleApprovalGroups = (approvalGroups: Group[]) => {
    setState(prev => ({
      ...prev,
      approvalGroups: approvalGroups.map(i => i.id),
    }));
  };

  const changeStorageAccount = e => {
    const storage = { ...state.storage };
    const { name, value } = e.target;
    setStorageError(prev => ({ ...prev, [name]: null }));

    if (!value) {
      delete storage[name];
    } else {
      storage[name] = value.trim();
    }

    setState(current => ({
      ...current,
      storage,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (Boolean(aliasesError)) return;
    setIsSaving(true);
    try {
      const { isValid, errors } = await validateAccess(state.storage);

      if (!isValid) return setStorageError(errors);

      const mappedState = { ...state };

      if (account) {
        await actions.updateAccount(account.email, mappedState);
      } else {
        await actions.createAccount(mappedState);
      }

      onClose();
    } catch (error) {
      const { type, error: message } = handleValidationErrors(state, error, t);
      if (type === "STORAGE") return setStorageError(message);
      if (type === "EMAIL") return setEmailError(message);
      if (type === "ALIASES") return setAliasesError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClick={e => e.stopPropagation()}
      onClose={onClose}
      TransitionProps={{ unmountOnExit: true }}
      scroll="paper"
    >
      <DialogTitle>
        {account ? t("common:edit") : t("common:add")} Account
      </DialogTitle>

      <DialogContent>
        <StyledTextField
          fullWidth
          inputProps={{
            "data-testid": `${
              account ? "edit-email-input" : "account-email-input"
            }`,
          }}
          label={t("email")}
          onChange={handleChange}
          required
          name="email"
          type="email"
          value={state.email}
          variant="outlined"
          disabled={Boolean(account)}
          error={Boolean(emailError)}
          helperText={emailError}
        />
        <TagsInput
          options={accountsState.tags}
          values={state.tags}
          onChange={handleAddTag}
        />
        <StyledChipInput
          fullWidth
          label={t("alias_plural")}
          onAdd={handleAddAlias}
          onDelete={handleDeleteAlias}
          variant="outlined"
          value={state.aliases}
          error={Boolean(aliasesError)}
          helperText={aliasesError || t("enter_to_add")}
          newChipKeys={["Enter", ","]}
        />

        {isLoading ? (
          <LoadingGroups />
        ) : (
          <ApprovalGroupSelector
            value={approvalGroups}
            onChange={handleApprovalGroups}
          />
        )}

        <StyledTextField
          fullWidth
          inputProps={{
            "data-testid": "accounts__account-box-input",
          }}
          label={t("orgStorageAccount.label", { value: "Box" })}
          onChange={changeStorageAccount}
          type="text"
          name="box"
          value={state.storage.box || ""}
          variant="outlined"
          error={Boolean(storageError.box)}
          helperText={storageError.box}
        />
        <StyledTextField
          fullWidth
          inputProps={{
            "data-testid": "accounts__account-dropbox-input",
          }}
          label={t("buttons.storageAccount", { value: "Dropbox" })}
          onChange={changeStorageAccount}
          type="text"
          name="dropbox"
          value={state.storage.dropbox || ""}
          variant="outlined"
          error={Boolean(storageError.dropbox)}
          helperText={storageError.dropbox}
        />
        <StyledTextField
          fullWidth
          inputProps={{
            "data-testid": "accounts__account-ondedrive-input",
          }}
          label={t("orgStorageAccount.label", { value: "OneDrive" })}
          onChange={changeStorageAccount}
          type="text"
          name="onedrive"
          value={state.storage.onedrive || ""}
          variant="outlined"
          error={Boolean(storageError.onedrive)}
          helperText={storageError.onedrive}
        />
        <StyledTextField
          fullWidth
          inputProps={{
            "data-testid": "accounts__account-drive-input",
          }}
          label={t("orgStorageAccount.label", { value: "Google Drive" })}
          onChange={changeStorageAccount}
          type="text"
          name="drive"
          value={state.storage.drive || ""}
          variant="outlined"
          error={Boolean(storageError.drive)}
          helperText={storageError.drive}
        />
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="accounts__account-cancel-edit"
          onClick={onClose}
          color="primary"
        >
          {t("common:cancel")}
        </Button>
        <ButtonWithLoading
          disabled={isLoading}
          color="primary"
          data-testid={
            account ? "account-confirm-edit-btn" : "account-confirm-create-btn"
          }
          disableElevation
          type="submit"
          onClick={handleSubmit}
          variant="contained"
          loading={isSaving}
        >
          {account ? t("common:save") : t("common:create")}
        </ButtonWithLoading>
      </DialogActions>
    </Dialog>
  );
}

function LoadingGroups() {
  const { t } = useTranslation("accounts");
  return (
    <div>
      <Typography variant="caption" color="textSecondary">
        {t("approvalGroups.label")}
      </Typography>
      <Skeleton
        height="3rem"
        style={{ transform: "none", marginBottom: "1rem" }}
      />
    </div>
  );
}

const StyledTextField = styled(TextField)({
  marginBottom: "20px",
});

const StyledChipInput = styled(ChipInput)({
  marginBottom: "20px",
  "& .MuiFormHelperText-root": {
    marginBottom: 0,
  },
});

const initState = (account?: AccountToEdit): State => ({
  email: account?.email || "",
  storage: account?.storage || {},
  tags: account?.tags || [],
  aliases: account?.aliases || [],
  approvalGroupIds: account?.approvalGroups || [],
});

const initStorageError = (): AccountToEdit["storage"] => ({
  box: null,
  onedrive: null,
  egnyte: null,
  dropbox: null,
  drive: null,
});
